import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

interface PollSummary {
  pollId: string;
  title: string;
  status: string;
  resultsVisibility: string;
  closesAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UsePollsOptions {
  status?: string;
}

interface UsePollsReturn {
  polls: PollSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export function usePolls(options: UsePollsOptions = {}): UsePollsReturn {
  const [polls, setPolls] = useState<PollSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 20;
  const fetchIdRef = useRef(0);

  const fetchPolls = useCallback(
    async (newOffset: number = 0, append: boolean = false) => {
      const id = ++fetchIdRef.current;
      try {
        setLoading(true);
        const result = await api.listMyPolls({
          status: options.status || undefined,
          limit,
          offset: newOffset,
        });
        if (fetchIdRef.current !== id) return;
        if (append) {
          setPolls((prev) => [...prev, ...result.polls]);
        } else {
          setPolls(result.polls);
        }
        setTotal(result.total);
        setOffset(newOffset);
      } catch (err) {
        if (fetchIdRef.current !== id) return;
        setError(err instanceof Error ? err.message : 'Failed to load polls');
      } finally {
        if (fetchIdRef.current === id) {
          setLoading(false);
        }
      }
    },
    [options.status],
  );

  const refresh = useCallback(() => {
    setError(null);
    fetchPolls(0, false);
  }, [fetchPolls]);

  const loadMore = useCallback(() => {
    if (!loading && offset + limit < total) {
      fetchPolls(offset + limit, true);
    }
  }, [fetchPolls, loading, offset, total]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return {
    polls,
    total,
    loading,
    error,
    refresh,
    loadMore,
    hasMore: offset + limit < total,
  };
}
